import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, FileText, Upload, Loader2, Trash2, Download,
  FolderOpen, Plus, Search, Filter
} from "lucide-react";
import { API } from "@/App";

const TEMPLATE_CATEGORIES = [
  { id: "ta_forms", name: "TA Forms", icon: "📋", color: "bg-blue-100 text-blue-700" },
  { id: "contracts", name: "Contracts", icon: "📄", color: "bg-purple-100 text-purple-700" },
  { id: "enquiries", name: "Enquiries", icon: "❓", color: "bg-amber-100 text-amber-700" },
  { id: "searches", name: "Searches", icon: "🔍", color: "bg-green-100 text-green-700" },
  { id: "completion", name: "Completion", icon: "✅", color: "bg-emerald-100 text-emerald-700" },
  { id: "other", name: "Other", icon: "📁", color: "bg-slate-100 text-slate-700" }
];

export const SolicitorTemplatesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  
  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    name: "",
    category: "",
    description: "",
    file: null
  });

  // Delete confirmation
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCategory]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params = filterCategory && filterCategory !== "all" ? `?category=${filterCategory}` : "";
      const response = await axios.get(`${API}/documents/solicitor-templates${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.name || !uploadData.category) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("name", uploadData.name);
      formData.append("category", uploadData.category);
      formData.append("description", uploadData.description || "");
      formData.append("file", uploadData.file);
      
      await axios.post(`${API}/documents/solicitor-templates/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Template uploaded successfully");
      setShowUploadModal(false);
      setUploadData({ name: "", category: "", description: "", file: null });
      fetchTemplates();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload template");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTemplate) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/solicitor-templates/${deleteTemplate.template_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template deleted");
      setDeleteTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCategoryInfo = (categoryId) => {
    return TEMPLATE_CATEGORIES.find(c => c.id === categoryId) || TEMPLATE_CATEGORIES[5];
  };

  const groupedTemplates = TEMPLATE_CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = filteredTemplates.filter(t => t.category === cat.id);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Document Templates</h1>
                <p className="text-xs text-muted-foreground">Manage your firm&apos;s document templates</p>
              </div>
            </div>
            <Button onClick={() => setShowUploadModal(true)} data-testid="upload-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Upload Template
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {TEMPLATE_CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          {TEMPLATE_CATEGORIES.map(cat => (
            <Card 
              key={cat.id} 
              className={`cursor-pointer hover:shadow-md transition-shadow ${filterCategory === cat.id ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setFilterCategory(filterCategory === cat.id ? "all" : cat.id)}
            >
              <CardContent className="p-4 text-center">
                <div className="text-2xl mb-1">{cat.icon}</div>
                <p className="text-sm font-medium">{cat.name}</p>
                <p className="text-lg font-bold text-primary">
                  {templates.filter(t => t.category === cat.id).length}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchTerm ? "No templates match your search" : "No templates uploaded yet"}
              </p>
              <Button onClick={() => setShowUploadModal(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTemplates).map(([catId, catTemplates]) => {
              if (catTemplates.length === 0) return null;
              const catInfo = getCategoryInfo(catId);
              
              return (
                <div key={catId}>
                  <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <span>{catInfo.icon}</span>
                    {catInfo.name}
                    <Badge variant="outline">{catTemplates.length}</Badge>
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {catTemplates.map(template => (
                      <Card key={template.template_id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${catInfo.color}`}>
                                <FileText className="w-5 h-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{template.name}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {template.original_filename}
                                </p>
                                {template.description && (
                                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                    {template.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between mt-4 pt-3 border-t">
                            <p className="text-xs text-muted-foreground">
                              {new Date(template.created_at).toLocaleDateString('en-GB')}
                            </p>
                            <div className="flex items-center gap-1">
                              <a
                                href={template.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 hover:bg-slate-100 rounded"
                                title="Download"
                              >
                                <Download className="w-4 h-4 text-blue-600" />
                              </a>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="p-1.5 h-auto hover:bg-red-50"
                                onClick={() => setDeleteTemplate(template)}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-primary" />
              Upload Document Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={uploadData.name}
                onChange={(e) => setUploadData({ ...uploadData, name: e.target.value })}
                placeholder="e.g. TA6 Property Information Form"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Category *</Label>
              <Select
                value={uploadData.category}
                onValueChange={(v) => setUploadData({ ...uploadData, category: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                placeholder="Brief description of the template..."
                className="mt-1"
                rows={2}
              />
            </div>

            <div>
              <Label>File (PDF or Word) *</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setUploadData({ ...uploadData, file: e.target.files?.[0] || null })}
                className="mt-1 cursor-pointer"
              />
              {uploadData.file && (
                <p className="text-xs text-green-600 mt-1">
                  ✓ Selected: {uploadData.file.name}
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload} 
              disabled={uploading || !uploadData.file || !uploadData.name || !uploadData.category}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Template?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete &quot;{deleteTemplate?.name}&quot;? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTemplate(null)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SolicitorTemplatesPage;
